import { useCheckboxGroup } from '@chakra-ui/react'
import { mdiMenuSwap } from '@mdi/js'
import Icon from '@mdi/react'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Checkbox } from '@/components/ui/checkbox'
import { Flex } from '@/components/ui/flex'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import { itemsForBulkApprovalsAtom, sailPointCertMembersMaxAtom } from '@/store/tasksApprovals/detailsDrawer'
import { ISailPointCertDetails, ISailPointCertGroupEntry, ISailPointCertGroups } from '@/types/tasksApprovals'

import SailpointCertGroup from './group'

const SailpointCertGroups = ({ item }: { item: ISailPointCertDetails[] }) => {
    const { t } = useTranslation()
    const [userSortAsc, setUserSortAsc] = useState<boolean>(false)
    const [managerSortAsc, setManagerSortAsc] = useState<boolean>(false)
    const [allGroupsSelected, selectAllGroups] = useState(false)
    const [maxMembers, setMaxMembers] = useAtom(sailPointCertMembersMaxAtom)
    const [selectedMembers] = useAtom(itemsForBulkApprovalsAtom)
    const [isChecked, setIsChecked] = useState(false)
    const [isIndeterminate, setIsIndeterminate] = useState(false)

    const groupBy = (key: keyof ISailPointCertDetails) => {
        let groups: ISailPointCertGroups = {}

        item?.forEach((item) => {
            if (typeof item[key] === 'string' && key !== 'ratifiers' && key !== 'metadata') {
                const groupName = item[key]

                const groupInfo = {
                    description: item.description,
                    user: item.requester_name ? item.requester_name : t('tasksApprovals.sailPointCertDrawer.body.groups.notAvailable'),
                    manager: item.requester_manager_name
                        ? item.requester_manager_name
                        : t('tasksApprovals.sailPointCertDrawer.body.groups.notAvailable'),
                    isSelected: false,
                    isDisabled: item?.metadata?.isDisabled || false,
                    metadata: item.metadata || undefined,
                    entityId: item.approval_entity_id,
                }

                if (!groups) {
                    groups = {
                        [groupName]: [{ ...groupInfo }],
                    }
                    return
                }

                if (!groups[groupName]) {
                    groups[groupName] = [{ ...groupInfo }]
                } else {
                    groups[groupName].push({ ...groupInfo })
                }
            }
        })

        return groups
    }

    const [groups, setGroups] = useState<ISailPointCertGroups>(groupBy('title'))

    const sortBy = (sort: 'user' | 'manager') => {
        const sortedGroups = { ...groups }
        const _sortOrder = sort === 'user' ? userSortAsc : managerSortAsc
        if (!sortedGroups) return

        const sortAsc = (a: ISailPointCertGroupEntry, b: ISailPointCertGroupEntry) => {
            const elA = a[sort].toLowerCase()
            const elB = b[sort].toLowerCase()

            if (elA < elB) {
                return -1
            }
            if (elA > elB) {
                return 1
            }
            return 0
        }

        const sortDesc = (a: ISailPointCertGroupEntry, b: ISailPointCertGroupEntry) => {
            const elA = a[sort].toLowerCase()
            const elB = b[sort].toLowerCase()

            if (elA > elB) {
                return -1
            }
            if (elA < elB) {
                return 1
            }
            return 0
        }

        Object.keys(sortedGroups).map((group) => {
            sortedGroups[group] = sortedGroups[group].sort(_sortOrder ? sortAsc : sortDesc)
        })

        setGroups(sortedGroups)

        if (sort === 'user') {
            setUserSortAsc(!userSortAsc)
        } else if (sort === 'manager') {
            setManagerSortAsc(!managerSortAsc)
        }
    }

    const onSelectGroups = () => {
        if (isIndeterminate && allGroupsSelected) {
            selectAllGroups(false)
        } else if (isIndeterminate || !allGroupsSelected) {
            selectAllGroups(true)
        } else {
            selectAllGroups(false)
        }
    }

    const { getItemProps } = useCheckboxGroup({
        defaultValue: [],
    })

    useEffect(() => {
        setIsIndeterminate(selectedMembers.length < maxMembers && selectedMembers.length > 0)
        setIsChecked(selectedMembers.length === maxMembers && selectedMembers.length > 0)
        if (selectedMembers.length === maxMembers && selectedMembers.length > 0) {
            selectAllGroups(true)
        } else if (selectedMembers.length === 0) {
            selectAllGroups(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMembers])

    useEffect(() => {
        if (Object.keys(groups).length) {
            let count = 0
            Object.keys(groups).map((key) => {
                count += groups[key].length - groups[key].filter((member) => member.isDisabled).length
            })
            setMaxMembers(count)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups])

    return (
        <Box position="relative">
            <Flex
                flexGrow="1"
                flexDir="row"
                position="sticky"
                top="0"
                bg={useColorModeValue('white', 'dark.800')}
                zIndex="1"
                px="24px"
                mx="0"
                mb="24px"
            >
                <Flex borderBottom="1px solid" borderBottomColor="gray.300" width="calc(100%)" py="12px">
                    <Flex width="88px">
                        <Checkbox
                            {...getItemProps({ value: 'selectAll' })}
                            onCheckedChange={onSelectGroups}
                            checked={isIndeterminate ? 'indeterminate' : isChecked}
                        />
                    </Flex>
                    <Flex flexDir="row" flexGrow="1" maxW="513px">
                        <Flex width="50%">
                            <Flex alignItems="center" cursor="pointer" onClick={() => sortBy('user')}>
                                <Text fontSize="12px">{t('tasksApprovals.sailPointCertDrawer.body.groups.col_1')}</Text>
                                <Icon path={mdiMenuSwap} size={1} />
                            </Flex>
                        </Flex>
                        <Flex width="50%">
                            <Flex alignItems="center" cursor="pointer" onClick={() => sortBy('manager')}>
                                <Text fontSize="12px">{t('tasksApprovals.sailPointCertDrawer.body.groups.col_2')}</Text>
                                <Icon path={mdiMenuSwap} size={1} />
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
            {Object.keys(groups).map((key) => {
                return (
                    <SailpointCertGroup
                        title={key}
                        description={groups[key][0]['description']}
                        members={groups[key]}
                        key={key}
                        checkAll={allGroupsSelected}
                    />
                )
            })}
        </Box>
    )
}

export default SailpointCertGroups
